import { apiClient } from './api'
import { ApiResponse } from './api'

export interface ProjectAsset {
  _id: string
  name: string
  description: string
  assetType: string
  projectId: string
  tenantId: string
  webUrl?: string
  loginUrl?: string
  credentialEmail?: string
  credentialPassword?: string
  scanCount?: number
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
  deletedAt: string | null
  deletedBy: string | null
  __v: number
}

export interface CreateProjectAssetRequest {
  name: string
  description: string
  assetType: string
  projectId: string
  tenantId: string
  webUrl?: string
  loginUrl?: string
  credentialEmail?: string
  credentialPassword?: string
}

export interface UpdateProjectAssetRequest {
  name?: string
  description?: string
  assetType?: string
  webUrl?: string
  loginUrl?: string
  credentialEmail?: string
  credentialPassword?: string
}

export interface GetProjectAssetsParams {
  page?: number
  limit?: number
  search?: string
}

export interface ProjectAssetsPaginatedResponse {
  success: boolean
  message: string
  data: {
    data: ProjectAsset[]
    total: number
    page: number
    limit: number
  }
}

class ProjectAssetsApiService {
  private baseEndpoint = '/projects-service/api/v1/project-assets'

  async getProjectAssets(projectId: string, params?: GetProjectAssetsParams): Promise<ApiResponse<ProjectAsset[]>> {
    try {
      const queryParams = new URLSearchParams()
      
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)
      
      const response = await apiClient.get<ProjectAssetsPaginatedResponse>(`${this.baseEndpoint}/${projectId}?${queryParams.toString()}`, undefined, 'PROJECTS')
      
      // Transform the paginated response to match the expected format
      if (response.success && response.data) {
        return {
          success: response.success,
          message: response.message,
          data: (response.data as any).data || []
        }
      }
      
      return {
        success: false,
        message: 'Failed to fetch project assets',
        data: []
      }
    } catch (error) {
      console.error('Failed to fetch project assets:', error)
      throw error
    }
  }

  async getProjectAssetById(assetId: string): Promise<ApiResponse<ProjectAsset>> {
    try {
      const response = await apiClient.get<ProjectAsset>(`${this.baseEndpoint}/asset/${assetId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to fetch project asset:', error)
      throw error
    }
  }

  async createProjectAsset(assetData: CreateProjectAssetRequest): Promise<ApiResponse<ProjectAsset>> {
    try {
      const response = await apiClient.post<ProjectAsset>(this.baseEndpoint, assetData, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to create project asset:', error)
      throw error
    }
  }

  async updateProjectAsset(assetId: string, assetData: UpdateProjectAssetRequest): Promise<ApiResponse<ProjectAsset>> {
    try {
      const response = await apiClient.put<ProjectAsset>(`${this.baseEndpoint}/${assetId}`, assetData, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to update project asset:', error)
      throw error
    }
  }

  async deleteProjectAsset(assetId: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseEndpoint}/${assetId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to delete project asset:', error)
      throw error
    }
  }

  async getAssetDetails(assetId: string): Promise<ApiResponse<ProjectAsset & { vulnerabilityCounts: any }>> {
    try {
      const response = await apiClient.get<ProjectAsset & { vulnerabilityCounts: any }>(`${this.baseEndpoint}/asset/${assetId}`, undefined, 'PROJECTS')
      return response
    } catch (error) {
      console.error('Failed to get asset details:', error)
      throw error
    }
  }
}

export const projectAssetsApi = new ProjectAssetsApiService()
